import React, { useState, useEffect } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useDuplicateCompanyRole, CompanyRoleListItem } from 'hooks/api'
import { getRoleDisplayName } from 'constants/roles'

import { Stack } from '@mui/material'

import { Modal, Button, Input } from '@repo/ui'

interface DuplicateRoleModalProps {
  role: CompanyRoleListItem | null | undefined
  open: boolean
  onClose: () => void
  onDuplicated?: (newRole: CompanyRoleListItem) => void
}

export const DuplicateRoleModal = ({ role, open, onClose, onDuplicated }: DuplicateRoleModalProps) => {
  const { open: notification } = useSnackbar()
  const [name, setName] = useState('')

  useEffect(() => {
    if (open && role) {
      // Default to the display title (e.g. "Admin copy"), not the backend slug.
      setName(`${getRoleDisplayName(role)} copy`)
    }
  }, [open, role])

  const { mutate: duplicateRole, isPending } = useDuplicateCompanyRole({
    onSuccess: (newRole) => {
      onClose()
      onDuplicated?.(newRole)
    },
    onError: (error: any) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const handleSubmit = () => {
    if (!role || !name.trim()) return
    duplicateRole({
      id: role.id,
      newName: name.trim(),
      sourceRoleName: role.name,
    })
  }

  return (
    <Modal
      title="Duplicate role"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || isPending}>
            {isPending ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <Stack spacing={4}>
          <Input
            label="New role name"
            placeholder="Enter"
            value={name}
            onChange={(value: string) => setName(value)}
          />
          {/* Lets Enter submit the form (handleSubmit no-ops on an empty name). */}
          <button type="submit" style={{ display: 'none' }} aria-hidden />
        </Stack>
      </form>
    </Modal>
  )
}
